import React, { useState, useEffect } from "react";
import { COPY_STRINGS } from "Constants/translatedCopy";
import { sleep } from "../../shared/util";
import { Container } from "../../shared";
import useConversation from "../../shared/useConversation";
import useIsMounted from "../../shared/hooks/useIsMounted";
import { listen } from "Services/Listen";
import { getUserData } from "Services/API";
import ListeningStatus from "Components/Global/ListeningStatus";
import "../ChangeOnePart.scss";
import icon from "../changeonepart-icon.png";
import splitByLetter from "../../../functions/splitByLetter";
import splitByGraphemes from "../../../functions/splitByGraphemes";
import { EVENTS, colors } from "../constants";
import ChangeOnePartContent from "../ChangeOnePartContent";
import useActivityStage from "../hooks/useActivityStage";
import { getAudioForPhonemes } from "../../shared/phonemeToAudio";
import useCancellablePromise from "../../shared/hooks/useCancellablePromise";

let ttsStrings;

export default function ChangeOnePart(props) {
  ttsStrings = COPY_STRINGS.intervention_strings.change_one_part;

  // activity props
  const {
    assessmentMode,
    tutorMode,
    word,
    wordMetadata,
    speech,
    paused,
    didAttempt,
    versionId,
    isInstruction,
    isFTUE,
    updateFtueSeen,
    ftueNotSeen
  } = props;

  const { PHON, GRAPHEMIC_BREAKDOWN, SUB_POSITION, SUBS } = wordMetadata;
  const wordBreakdown = assessmentMode || tutorMode
    ? splitByLetter(word)
    : splitByGraphemes(GRAPHEMIC_BREAKDOWN);
  const targetWordBreakdown = assessmentMode || tutorMode
    ? splitByLetter(SUBS[0].word)
    : splitByGraphemes(SUBS[0].grapheme);

  const [colorIndex, setColorIndex] = useState(-1); // controls which phoneme is being spoken. (non-assessment-mode only)
  const [swapping, setSwapping] = useState(false); // controls the transition between the swap letters.
  const [subIndex, setSubIndex] = useState(0); // controls the current item, each of this activity can have multiple words (items).
  const [highlight, setHighlight] = useState(tutorMode ? null : colors.black); // controls the box highlighting.
  const [hidden, setHidden] = useState(tutorMode ? false : true); // controls visibility of the boxes

  const isStart = SUB_POSITION === "start"; // the sub / change position can only be start or end of the word
  const targetIndex = isStart ? 0 : wordBreakdown.length - 1; // the letter being swapped can only be the start or the end letter.

  const originalGrapheme = wordBreakdown[targetIndex];
  const targetGrapheme = targetWordBreakdown[targetIndex];

  const isMountedRef = useIsMounted();
  const { cancellablePromise } = useCancellablePromise();

  const { speak, speakPhonemes, speakFile, listenWord, speakSequence, startListening, stopListening } = useConversation(
    speech,
    listen
  );

  const getStageOneHandler = () => {
    if (assessmentMode) return handleStageOneAssessment;
    if (tutorMode) return handleStageOneTutor;
    return handleStageOneIntervention;
  };

  const getStageTwoHandler = () => {
    if (assessmentMode) return handleStageTwoAssessment;
    if (tutorMode) return handleStageTwoTutor;
    return () => {};
  }

  /**
   * Handles closing the intervention.
   */
  const handleClose = () => {
    if (!isMountedRef.current) return;

    props.close();
  }

  /**
   * Handles stage 0 for assessment, tutor and intervention modes.
   */
  const handleStageZero = async () => {
    if (!isMountedRef.current) return;

    try {
      // stage 0 is giving intro in assessment mode and example in intervention (default)
      const { isExample, phraseIndex } = SUBS[subIndex];
      if (assessmentMode || tutorMode) {
        stopListening();
        if(versionId === 2){
          await cancellablePromise(playAssessmentTutorModeInstructionsWithoutOrthography(isExample));
        }else{
          await cancellablePromise(playAssessmentTutorModeInstructionsWithOrthography(isExample, phraseIndex));
        }
      } else {
        await cancellablePromise(playInterventionModeInstructions());
      }
      // actions for any mode for stage 0
      setSwapping(true);
      await cancellablePromise(sleep(1200)); // the time for the animation to finish in css
      setStage(1);
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  };

  /**
   * Handles stage 1 for tutor mode.
   */
  const handleStageOneTutor = async () => {
    if (!isMountedRef.current) return;

    try {
      const { isExample } = SUBS[subIndex];

      await cancellablePromise(sleep(4000 / 3));
      setSwapping(false);

      if(isFTUE && ftueNotSeen) {
        await cancellablePromise(speak(ttsStrings.tutor_example_change_one_FTUE));
        await cancellablePromise(speak({textToSpeak: SUBS[subIndex].word, skipTranslation: getUserData().bilingualMode}));
        await cancellablePromise(sleep());
        updateFtueSeen();
        setStage(0);
      } else if (isExample && !ftueNotSeen) {
        await cancellablePromise(speak(ttsStrings.tutor_example_ask_word));
      } else {
        await cancellablePromise(speak(ttsStrings.tutor_subsequent_ask_word));
      }

      if(!isFTUE || !ftueNotSeen) {
        setHighlight(colors.yellow);
        speech.startListening();
        // notify parent to start the phrase
        props.dispatchEvent(EVENTS.start);
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  };

  /**
   * Handles stage 1 for intervention mode.
   * @returns
   */
  const handleStageOneIntervention = async () => {
    if (!isMountedRef.current) return;

    try {
      const { word: sub, phon } = SUBS[subIndex];

      if (subIndex !== 0) {
        await cancellablePromise(speak(ttsStrings.another_swap));
      }

      await cancellablePromise(sleep(4000 / 3));
      setSwapping(false);

      await cancellablePromise(speak(ttsStrings.ask_word));

      // start handling listening to user speech
      setHighlight(colors.yellow);
      const { status } = await cancellablePromise(listenWord({
        targetWord: sub,
        patterOverride: ["match", "struggle", "silence"],
      }));
      // finished listening to the user
      setHighlight(null);

      // start handling result of the user speech
      if (status === "skip") {
        return handleClose();
      }

      if (status === "match") {
        // flash green
        setHighlight(colors.green);
        if (subIndex < SUBS.length - 1) {
          await cancellablePromise(speak(ttsStrings.correct));
        }
        await cancellablePromise(sleep());
      } else {
        // on struggle or silence
        await cancellablePromise(speak(ttsStrings.sound_out));
        await cancellablePromise(sleep(500));
        await cancellablePromise(speakPhonemes({ phon, onSpeak: setColorIndex }));
        await cancellablePromise(speak(sub));
        setColorIndex(-1);
      }

      // start handling transition to the next SUB activity
      setHighlight(null);

      // if not the last sub item
      if (subIndex < SUBS.length - 1) {
        // continue to next SUB item
        setSwapping(true);
        setSubIndex(subIndex + 1);
      } else {
        // end of all available SUBS
        await cancellablePromise(speak(ttsStrings.good_job));
        handleClose();
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  };

  const handleStageOneAssessment = async () => {
    if (!isMountedRef.current) return;

    try {
      const { isExample, phraseIndex } = SUBS[subIndex];
      const newWord = SUBS[subIndex].word;

      if (isExample) {
        if(versionId === 2){
          // do nothing
        }else{
          await cancellablePromise(speak(ttsStrings.assessment_handle_intro_prompt_to_try_transition));
        }
        if (getUserData().bilingualMode) {
          await cancellablePromise(speakSequence([
            ttsStrings.assessment_handle_intro_prompt_to_try_read,
            { textToSpeak: newWord, skipTranslation: true },
          ]));
        } else {
          await cancellablePromise(speak(
            ttsStrings.assessment_handle_intro_prompt_to_try_read + " " + newWord
          ));
        }
      } else {
        if (phraseIndex === 1) {
          await cancellablePromise(speak(ttsStrings.assessment_handle_first_item_prompt_to_read));
        } else if (phraseIndex > 1) {
          await cancellablePromise(speak(ttsStrings.assessment_handle_nth_item_prompt_to_read));
        }
      }
      setHighlight(colors.yellow);
      startListening();

      props.dispatchEvent(EVENTS.start);
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  };

  const handleStageTwoAssessment = async () => {
    if (!isMountedRef.current) return;

    try {
      const { isExample } = SUBS[subIndex];
      const newWord = SUBS[subIndex].word;

      stopListening();
      setSwapping(true);
      if (isExample) {
        if (getUserData().bilingualMode) {
          await cancellablePromise(speakSequence([
            ttsStrings.assessment_handle_intro_silence,
            { textToSpeak: newWord, skipTranslation: true },
          ]));
        } else {
          await cancellablePromise(speak(ttsStrings.assessment_handle_intro_silence + " " + newWord));
        }
      } else {
        await cancellablePromise(speak(ttsStrings.assessment_handle_silence));
      }
      setSwapping(false);
      setHighlight(colors.yellow);
      startListening();

      // notify parent to that retry started
      props.dispatchEvent(EVENTS.retry);
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  };

  // stage 2 tutor, handle pause / silence
  const handleStageTwoTutor = async () => {
    if (!isMountedRef.current) return;

    try {
      speech.stopListening();
      await cancellablePromise(speak(ttsStrings.tutor_silent_ask_word));
      speech.startListening();
      // notify parent to that retry started
      props.dispatchEvent(EVENTS.retry);
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  const handleStageThreeAssessment = async () => {
    if (!isMountedRef.current) return;

    props.dispatchEvent(EVENTS.end);
    handleClose();
  };


  const playAssessmentTutorModeInstructionsWithoutOrthography = async (isExample) => {
    if (!isMountedRef.current) return;

    try {
      const exampleTTS = ttsStrings.non_ortho.example;
      const subsequentTTS = ttsStrings.non_ortho.subsequent;
      const files = getAudioForPhonemes(PHON, getUserData().locale);
      const sound = files[targetIndex];
      const subFiles = getAudioForPhonemes(SUBS[subIndex].phon, getUserData().locale);
      const subSound = subFiles[targetIndex];

      if (isExample) { // handles the first phrase item (example)
        // handles the first phrase item (example)
        if(getUserData().bilingualMode){ // example speeches for bilingual mode
          // TODO: bilingual mode doesn't seem to turn TRUE when making a student practice in Spanish and Both English and Spanish
          await cancellablePromise(speakSequence([
            exampleTTS.we_are_now,
            exampleTTS.these_boxes_represent,
            { textToSpeak: word, skipTranslation: true },
            exampleTTS.those_sounds_are,
          ]));
          await cancellablePromise(speakPhonemes({ phon: PHON }));
          await cancellablePromise(speakSequence([
            exampleTTS.im_going_to_change,
            { textToSpeak: word, skipTranslation: true },
            exampleTTS.im_going_to_change_2,
          ]));
          await cancellablePromise(speakFile(sound));
          await cancellablePromise(speak(exampleTTS.sound_to_the));
          await cancellablePromise(speakFile(subSound));
          await cancellablePromise(speak(exampleTTS.sound_which_will_make));
          await cancellablePromise(speakSequence([
            exampleTTS.when_you_see_the_boxes,
          ]));
          await cancellablePromise(speakFile(sound));
          await cancellablePromise(speakSequence([
            exampleTTS.in,
            { textToSpeak: word, skipTranslation: true },
            exampleTTS.to_a,
          ]));
          await cancellablePromise(speakFile(subSound));
        }else{ // example speeches English only mode
          // example speeches English only mode
          await cancellablePromise(speak(exampleTTS.we_are_now));
          await cancellablePromise(sleep());
          // These three boxes represent the three sounds in the word "cat". Those three sounds are c a t.
          await cancellablePromise(speak(`${exampleTTS.these_boxes_represent} "${word}". ${exampleTTS.those_sounds_are} ${wordBreakdown[0]} ${wordBreakdown[1]} ${wordBreakdown[2]}.`))
          await cancellablePromise(sleep());
          // I'm going to change one sound in the word "cat". I'm going to change the c sound to the b sound, which will make a new word.
          await cancellablePromise(speak(`${exampleTTS.im_going_to_change} "${word}". ${exampleTTS.im_going_to_change_2} ${originalGrapheme} ${exampleTTS.sound_to_the} ${targetGrapheme} ${exampleTTS.sound_which_will_make}`));
          await cancellablePromise(sleep());
          // When you see the boxes turn yellow, you should say the new word that it makes if I change the c in "cat" to a b.
          await cancellablePromise(speak(`${exampleTTS.when_you_see_the_boxes} ${originalGrapheme} ${exampleTTS.in} "${word}" ${exampleTTS.to_a} ${targetGrapheme}.`));
        }
      } else { // handles the subsequent phrase items
        if(getUserData().bilingualMode){ // subsequent speeches for bilingual mode
          await cancellablePromise(speakSequence([
            ttsStrings.assessment_handle_intro_say_word,
            subsequentTTS.now_lets_play_with,
            { textToSpeak: word, skipTranslation: true },
            subsequentTTS.these_boxes_represent,
            { textToSpeak: word, skipTranslation: true },
            subsequentTTS.those_sounds_are,
          ]));
          await cancellablePromise(speakPhonemes({ phon: PHON }));
          await cancellablePromise(speak(subsequentTTS.lets_change));
          await cancellablePromise(speakFile(sound));
          await cancellablePromise(speak(subsequentTTS.sound_to_the));
          await cancellablePromise(speakFile(subSound));
          await cancellablePromise(speak(subsequentTTS.sound));

        }else{ // subsequent speeches English only mode
          // Now let's play with the word "net".
          await cancellablePromise(speak(`${subsequentTTS.now_lets_play_with} "${word}".`));
          await cancellablePromise(sleep());
          // These three boxes represent the three sounds in the word "net".
          await cancellablePromise(speak(`${subsequentTTS.these_boxes_represent} "${word}".`));
          await cancellablePromise(sleep());
          // Those three sounds are n e t.
          await cancellablePromise(speak(`${subsequentTTS.those_sounds_are} ${wordBreakdown[0]} ${wordBreakdown[1]} ${wordBreakdown[2]}.`));
          await cancellablePromise(sleep());
          // Now let's change the n sound to the g sound.
          await cancellablePromise(speak(`${subsequentTTS.lets_change} ${originalGrapheme} ${subsequentTTS.sound_to_the} ${targetGrapheme} ${subsequentTTS.sound}.`));
        }
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  const playAssessmentTutorModeInstructionsWithOrthography = async (isExample, phraseIndex) => {
    if (!isMountedRef.current) return;

    try {
      // stage 0 is giving intro in assessment mode and example in intervention (default)
      if (assessmentMode) {
        stopListening();
        // start handling assessment mode Amira speech
        if (isExample) {
          // handles the first phrase item (example)
          await cancellablePromise(speak(ttsStrings.assessment_handle_intro));
          await cancellablePromise(sleep());
          if (getUserData().bilingualMode) {
            await cancellablePromise(speakSequence([
              ttsStrings.assessment_handle_intro_say_word,
              { textToSpeak: word, skipTranslation: true },
            ]));
          } else {
            await cancellablePromise(speak(ttsStrings.assessment_handle_intro_say_word + " " + word));
          }
          await cancellablePromise(sleep());
          await cancellablePromise(speak(ttsStrings.assessment_handle_intro_make_change));
        } else {
          // handles the rest of the phrases in story (not example)
          if (phraseIndex === 1) {
            if (getUserData().bilingualMode) {
              await cancellablePromise(speakSequence([
                ttsStrings.assessment_handle_first_item_prompt,
                { textToSpeak: word, skipTranslation: true },
              ]));
            } else {
              await cancellablePromise(speak(
                ttsStrings.assessment_handle_first_item_prompt + " " + word
              ));
            }
            await cancellablePromise(sleep());
            await cancellablePromise(speak(ttsStrings.assessment_handle_first_item_make_change));
          } else if (phraseIndex > 1) {
            if (getUserData().bilingualMode) {
              await cancellablePromise(speakSequence([
                ttsStrings.assessment_handle_nth_item_prompt,
                { textToSpeak: word, skipTranslation: true },
              ]));
            } else {
              await cancellablePromise(speak(
                ttsStrings.assessment_handle_nth_item_prompt + " " + word
              ));
            }
            await cancellablePromise(sleep());
            await cancellablePromise(speak(ttsStrings.assessment_handle_nth_item_make_change));
          }
        }
      } else if (tutorMode) {
        stopListening();
        if(isFTUE && ftueNotSeen) {
          await cancellablePromise(speak(ttsStrings.tutor_example_intro_FTUE));
          await cancellablePromise(sleep());
          await cancellablePromise(speak({textToSpeak: word, skipTranslation: getUserData().bilingualMode}));
        } else if (isExample && !ftueNotSeen) {
          await cancellablePromise(speak(ttsStrings.tutor_example_intro));
          await cancellablePromise(speak({textToSpeak: word, skipTranslation: getUserData().bilingualMode}));
          setColorIndex(-1); // setting color index is to highlight the phoneme being spoken
          await cancellablePromise(speak(ttsStrings.tutor_example_change_one));
        }
        await cancellablePromise(sleep());
      }
      // actions for any mode for stage 0
      setSwapping(true);
      setStage(1);
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  const playInterventionModeInstructions = async () => {
    if (!isMountedRef.current) return;

    try {
      await cancellablePromise(speak(ttsStrings.intro));
      await cancellablePromise(sleep());
      await cancellablePromise(speakPhonemes({ phon: PHON, onSpeak: setColorIndex }));
      setColorIndex(-1); // setting color index is to highlight the phoneme being spoken
      await cancellablePromise(speak(ttsStrings.change_one));
      await cancellablePromise(sleep());
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  const {stage, setStage} = useActivityStage({ subIndex,
    setHidden,
    setHighlight,
    handleStageZero: handleStageZero,
    handleStageOne: getStageOneHandler(),
    handleStageTwo: getStageTwoHandler(),
    handleStageThree: handleStageThreeAssessment,
    isInstruction,
  });

  useEffect(() => {
    // handles pause and attempt events for both assessment and tutor mode
    if(!assessmentMode && !tutorMode) return;

    if (didAttempt) {
      setStage(3); // handle ending
    } else if (paused) {
      setStage(2); // handle pause
    }
  }, [paused, didAttempt]);

  return (
    <Container icon={icon}>
      <ChangeOnePartContent {...{
        stage,
        wordBreakdown,
        colorIndex,
        targetIndex,
        swapping,
        hidden,
        highlight,
        subIndex,
        assessmentMode,
        SUBS,
        isStart,
        versionId }} />
      <div style={{ marginTop: 10 }}>
        {!assessmentMode && !tutorMode && <ListeningStatus />}
      </div>
    </Container>
  );
}